import type { Grossbild } from '../api/types'
import { istAppleMitFinger } from './geraet'

/**
 * Ein Foto aus der Anwendung heraus sichern — **ein Knopf, zwei Systeme**.
 *
 * **Warum das nicht der Browser erledigt.** Auf iOS legt ein langes Drücken
 * das Bild in „Fotos"; auf Android passiert dabei nichts (#58). Eine Geste,
 * die auf dem einen Gerät etwas tut und auf dem anderen nicht, ist keine
 * Bedienung, auf die sich eine Anwendung stützen kann.
 *
 * **Und der erste Anlauf war trotzdem falsch.** Er fragte überall zuerst das
 * Teilen-Blatt (`navigator.share`), weil beide Systeme es können. Aus dem
 * Betrieb kam die Rückmeldung von Android-Nutzern: dort steht darin nur
 * „Speichern in Google Bilder" — die Samsung Gallery taucht nicht auf.
 *
 * **Der Grund ist ein Unterschied der Systeme und kein Fehler hier.** Androids
 * Teilen-Blatt reicht Dateien an *Apps* weiter, die sich als Empfänger
 * eingetragen haben; ein „auf dem Gerät sichern" kennt es gar nicht. Was dort
 * wirklich in jeder Galerie auftaucht, ist der Ordner **Downloads** — und
 * dorthin kommt eine Datei über einen gewöhnlichen Download. Auf iOS ist es
 * genau andersherum: das Teilen-Blatt hat „Bild sichern" und legt es in
 * *Fotos*, ein Download landet in *Dateien*.
 *
 * **Also entscheidet nicht die Fähigkeit, sondern das System** — und das ist
 * hier ausnahmsweise richtig so: beide *können* teilen, sie tun damit nur
 * Verschiedenes. Eine Abfrage der Fähigkeit könnte das nicht auseinanderhalten
 * (`lib/geraet.ts` erklärt, warum diese Ausnahme eine ist).
 *
 *  - **iPhone und iPad:** das Teilen-Blatt. Dort heißt „Sichern" wirklich
 *    sichern.
 *  - **Alles andere** (Android, jeder Rechner): herunterladen. Auf Android
 *    landet es in *Downloads*, und den zeigt jede Galerie-App als Album.
 *
 * **Zwei Knöpfe wären die schlechtere Antwort gewesen** — aus dem Betrieb, und
 * knapp: „ich will nicht 2 Buttons - das verwirrt ja nur". Stimmt: wer ein
 * Foto sichern will, will nicht vorher wissen müssen, wie sein System das
 * intern nennt.
 *
 * **Geholt wird über dieselbe Adresse wie das angezeigte Bild**, also durch
 * `Visibility` hindurch. Das ist kein Umweg, sondern der Punkt: es gibt keinen
 * zweiten Weg zu einer Datei, und dieser hier bekommt keinen.
 */
export type Weg = 'geteilt' | 'geladen' | 'abgebrochen'

export async function sichereFoto(media: Grossbild): Promise<Weg> {
  // Same-Origin: das Session-Cookie geht von selbst mit. Und das Bild steht
  // schon im Zwischenspeicher des Browsers -- es wird gerade angezeigt --,
  // deshalb ist das im Regelfall keine zweite Übertragung.
  const antwort = await fetch(media.url)
  if (!antwort.ok) throw new Error(`Der Server antwortete mit ${antwort.status}.`)
  const inhalt = await antwort.blob()
  const datei = new File([inhalt], dateiname(media), { type: inhalt.type || media.mime })

  /*
   * **Erst das System, dann die Fähigkeit.** `canShare` allein genügt nicht:
   * Android kann es auch, und dort ist es der falsche Weg. Und umgekehrt darf
   * die Fähigkeit nicht fehlen — ein altes iOS ohne Dateifreigabe bekommt den
   * Download, statt in eine Ausnahme zu laufen.
   */
  if (istAppleMitFinger() && navigator.canShare?.({ files: [datei] }) === true) {
    try {
      await navigator.share({ files: [datei] })
      return 'geteilt'
    } catch (fehler) {
      // Wer das Blatt zumacht, hat nicht gescheitert -- er wollte nicht.
      if (abgebrochen(fehler)) return 'abgebrochen'
      // Alles andere ist ein echter Fehlschlag des einen Weges; dann bleibt
      // der andere. Eine Fehlermeldung, wo ein Download getan hätte, wäre
      // eine stille Absage mit Text.
    }
  }

  const adresse = URL.createObjectURL(inhalt)
  const verweis = document.createElement('a')
  verweis.href = adresse
  verweis.download = datei.name
  document.body.append(verweis)
  verweis.click()
  verweis.remove()
  // Erst freigeben, wenn der Browser den Download angefasst hat. Sofort
  // widerrufen heisst auf manchen Geräten: leere Datei.
  setTimeout(() => URL.revokeObjectURL(adresse), 60_000)
  return 'geladen'
}

/**
 * „reisefoto-42.jpg" — ein Name, den man wiederfindet.
 *
 * Der ursprüngliche Dateiname liegt nirgends: hochgeladen wird ein Bild, das
 * der Browser vorher verkleinert hat, und `IMG_4711.HEIC` beschreibt danach
 * ohnehin etwas anderes. Die Nummer ist dafür eindeutig, und die Endung kommt
 * aus dem Typ, den der Server nennt.
 */
export function dateiname(media: Grossbild): string {
  return `reisefoto-${media.id}.${endung(media.mime)}`
}

const ENDUNGEN: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
}

function endung(mime: string): string {
  const bekannt = ENDUNGEN[mime.toLowerCase()]
  if (bekannt !== undefined) return bekannt
  // `image/etwas-neues` -- der hintere Teil ist als Endung besser als „bin".
  const hinten = mime.split('/')[1]?.replace(/[^a-z0-9]/gi, '') ?? ''
  return hinten === '' ? 'jpg' : hinten.toLowerCase()
}

/** Das Teilen-Blatt zugemacht — kein Fehler, sondern eine Entscheidung. */
function abgebrochen(fehler: unknown): boolean {
  return fehler instanceof DOMException && fehler.name === 'AbortError'
}
